import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';

import { AuthGuard } from '../../app/AuthGuard';
import {
  customerCabinetApi,
  type ChatDto,
  type MessageDto,
  type OfferDto,
  type TransportDto,
} from '../../features/customerCabinet/api';
import {
  ChatAppContainer,
  initials,
  UnifiedChatComposer,
  UnifiedChatHeader,
  UnifiedChatSidebar,
  UnifiedMessageList,
} from '../../features/chat/UnifiedChatComponents';
import { mergeMessages, updateChatWithMessage } from '../../features/chat/messageUtils';
import { type ChatRealtimeEvent, useReconnectingWebSocket } from '../../features/chat/realtime';
import {
  CustomerCabinetLayout,
  navigateCustomer,
  type CustomerCabinetScreenId,
  type CustomerFigmaNodes,
} from '../../layouts/CustomerCabinet/CustomerCabinetLayout';
import { useAuth } from '../../state/auth';
import { CabinetEmptyState, CabinetLoadState } from '../../ui';
import { useChatStatus } from '../../state/chatStatus';
import { CustomerPageHeading, hashNumber } from './customerCabinetShared';
import './customer-cabinet.css';

export type CustomerMessageScreenId = Extract<CustomerCabinetScreenId, 'chat-list' | 'chat'>;

const FIGMA_NODES: Record<CustomerMessageScreenId, CustomerFigmaNodes> = {
  'chat-list': { '1920': '2680:39971', '1440': '2680:40277', '768': '2617:17345', '375': '2617:17499' },
  chat: { '1920': '2680:39902', '1440': '2680:40211', '768': '2584:17345', '375': '2586:17345' },
};

const GEOMETRY: Record<CustomerMessageScreenId, {
  height: Record<'desktop' | 'tablet' | 'mobile', number>;
  offset: Record<'desktop' | 'tablet' | 'mobile', number>;
}> = {
  'chat-list': { height: { desktop: 977, tablet: 482, mobile: 481 }, offset: { desktop: 0, tablet: 0, mobile: 0 } },
  chat: { height: { desktop: 1244, tablet: 968, mobile: 816 }, offset: { desktop: 0, tablet: 75, mobile: 45 } },
};

function MessageLayout({ children, screen }: { children: ReactNode; screen: CustomerMessageScreenId }) {
  const geometry = GEOMETRY[screen];
  return (
    <CustomerCabinetLayout
      contentHeight={geometry.height}
      figmaNode={FIGMA_NODES[screen]}
      screen={screen}
      topOffset={geometry.offset}
    >
      {children}
    </CustomerCabinetLayout>
  );
}

function ChatListView() {
  const auth = useAuth();
  const chatStatus = useChatStatus();
  const [chats, setChats] = useState<ChatDto[]>([]);
  const [query, setQuery] = useState('');
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');

  useEffect(() => {
    let active = true;
    const fetchChats = async () => {
      try {
        const items = await customerCabinetApi.chats();
        if (!active) return;
        setChats(items);
        setState('ready');
      } catch {
        if (!active) return;
        setState('error');
      }
    };
    void fetchChats();
    return () => {
      active = false;
    };
  }, [auth.user?.id, chatStatus.eventVersion]);

  return (
    <MessageLayout screen="chat-list">
      <div className="customer-chat-list" data-state={state}>
        <CustomerPageHeading
          figmaNode="2680:40281"
          title="Сообщения"
          subtitle="Приватные чаты с перевозчиками по вашим заявкам"
        />
        <CabinetLoadState state={state} />
        {state === 'ready' && !chats.length ? <CabinetEmptyState kind="messages" /> : null}
        {chats.length > 0 ? <ChatAppContainer isCompactList>
          <UnifiedChatSidebar
            chats={chats}
            partnerNameKey="carrier_name"
            query={query}
            setQuery={setQuery}
            onSelectChat={(chat) => navigateCustomer('chat', { chat: chat.id })}
          />
          <div className="customer-chat-placeholder">← Выберите чат</div>
        </ChatAppContainer> : null}
      </div>
    </MessageLayout>
  );
}

function ConversationView() {
  const auth = useAuth();
  const chatStatus = useChatStatus();
  const [activeChatId, setActiveChatId] = useState<number | null>(() => hashNumber('chat'));
  const requestedTransportId = hashNumber('transport');
  const messageViewport = useRef<HTMLDivElement>(null);
  const [chats, setChats] = useState<ChatDto[]>([]);
  const [chat, setChat] = useState<ChatDto | null>(null);
  const [messages, setMessages] = useState<MessageDto[]>([]);
  const [offers, setOffers] = useState<OfferDto[]>([]);
  const [transports, setTransports] = useState<TransportDto[]>([]);
  const [query, setQuery] = useState('');
  const [draft, setDraft] = useState('');
  const sendInFlight = useRef(false);
  const [state, setState] = useState<'loading' | 'ready' | 'sending' | 'error'>('loading');
  const [nextBefore, setNextBefore] = useState<number | null>(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [newMessages, setNewMessages] = useState(0);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [attachmentError, setAttachmentError] = useState('');
  const [failedAttachment, setFailedAttachment] = useState<File | null>(null);

  useEffect(() => {
    const handleHash = () => {
      const id = hashNumber('chat');
      if (id) setActiveChatId(id);
    };
    window.addEventListener('hashchange', handleHash);
    return () => window.removeEventListener('hashchange', handleHash);
  }, []);

  useEffect(() => {
    let active = true;
    const load = async () => {
      const [nextChats, nextTransports, nextOffers] = await Promise.all([
        customerCabinetApi.chats(),
        customerCabinetApi.transports(),
        customerCabinetApi.offers(),
      ]);
      const requestedTransport = nextTransports.find((item) => item.id === requestedTransportId);
      const nextChat =
        (activeChatId ? nextChats.find((item) => item.id === activeChatId) : null) ??
        nextChats.find((item) => item.deal === requestedTransport?.deal) ??
        nextChats[0] ??
        null;
      const page = nextChat
        ? await customerCabinetApi.messagesPage(nextChat.id)
        : { results: [], next_before: null };
      if (!active) return;
      setChats(nextChats);
      setChat(nextChat);
      setMessages(page.results);
      setNextBefore(page.next_before);
      setTransports(nextTransports);
      setOffers(nextOffers);
      setState('ready');
      const latest = page.results.at(-1);
      if (nextChat && latest) {
        await customerCabinetApi.markChatRead(nextChat.id, latest.id);
        if (active) {
          setChats((current) =>
            current.map((item) =>
              item.id === nextChat.id ? { ...item, unread_count: 0 } : item,
            ),
          );
          void chatStatus.refresh().catch(() => {});
        }
      }
    };
    load().catch(() => {
      if (active) setState('error');
    });
    return () => {
      active = false;
    };
  }, [activeChatId, requestedTransportId]);

  function isNearBottom() {
    const viewport = messageViewport.current;
    return !viewport || viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight < 80;
  }

  function scrollToBottom() {
    const viewport = messageViewport.current;
    if (viewport) viewport.scrollTop = viewport.scrollHeight;
    setNewMessages(0);
  }

  async function syncLatest() {
    if (!chat) return;
    const [page, nextChats] = await Promise.all([
      customerCabinetApi.messagesPage(chat.id),
      customerCabinetApi.chats(),
    ]);
    setMessages((current) => mergeMessages(current, page.results));
    setChats(nextChats);
    const latest = page.results.at(-1);
    if (latest) {
      await customerCabinetApi.markChatRead(chat.id, latest.id);
      setChats((current) =>
        current.map((item) => (item.id === chat.id ? { ...item, unread_count: 0 } : item)),
      );
      await chatStatus.refresh();
    }
  }

  const socketState = useReconnectingWebSocket<ChatRealtimeEvent>(
    chat ? `/ws/chats/${chat.id}/` : null,
    (event) => {
      if (!chat || event.chat_id !== chat.id) return;
      if (event.event === 'chat.message.created') {
        const message = event.message as MessageDto;
        const follow = isNearBottom();
        setMessages((current) => mergeMessages(current, [message]));
        setChats((current) =>
          updateChatWithMessage(current, chat.id, message, auth.user?.id ?? 0),
        );
        if (message.sender !== auth.user?.id) {
          void customerCabinetApi
            .markChatRead(chat.id, message.id)
            .then(() => {
              setChats((current) =>
                current.map((item) =>
                  item.id === chat.id ? { ...item, unread_count: 0 } : item,
                ),
              );
              return chatStatus.refresh();
            })
            .catch(() => {});
          if (!follow) setNewMessages((count) => count + 1);
        }
        if (follow) window.requestAnimationFrame(scrollToBottom);
        return;
      }
      if (event.user_id !== auth.user?.id) {
        setMessages((current) =>
          current.map((message) =>
            message.sender === auth.user?.id && message.id <= event.last_read_message_id
              ? { ...message, is_read_by_recipient: true }
              : message,
          ),
        );
      }
    },
    () => {
      void syncLatest().catch(() => {});
    },
  );

  useEffect(() => {
    if (!chat || socketState === 'open') return;
    const timer = window.setInterval(() => {
      void syncLatest().catch(() => {});
    }, 10000);
    return () => window.clearInterval(timer);
  }, [chat?.id, socketState]);

  useEffect(() => {
    if (state === 'ready') window.requestAnimationFrame(scrollToBottom);
  }, [chat?.id]);

  const transport = useMemo(
    () => transports.find((item) => item.deal === chat?.deal),
    [chat, transports],
  );
  const offer = useMemo(
    () => (chat?.offer ? offers.find((item) => item.id === chat.offer) : null),
    [chat?.offer, offers],
  );

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!chat || !draft.trim() || state === 'sending' || sendInFlight.current) return;
    sendInFlight.current = true;
    const outgoing = draft.trim();
    setState('sending');
    try {
      const message = await customerCabinetApi.sendMessage(chat.id, outgoing);
      setDraft('');
      setMessages((current) => mergeMessages(current, [message]));
      setChats((current) =>
        updateChatWithMessage(current, chat.id, message, auth.user?.id ?? 0),
      );
      setState('ready');
      window.requestAnimationFrame(scrollToBottom);
    } catch {
      setState('error');
    } finally {
      sendInFlight.current = false;
    }
  }

  async function attach(file?: File) {
    if (!chat || !file) return;
    setAttachmentError('');
    setFailedAttachment(null);
    setUploadProgress(0);
    try {
      const media = await customerCabinetApi.uploadChatAttachment(file, setUploadProgress);
      const message = await customerCabinetApi.sendMessage(chat.id, file.name, media);
      setMessages((current) => mergeMessages(current, [message]));
      setChats((current) =>
        updateChatWithMessage(current, chat.id, message, auth.user?.id ?? 0),
      );
      setUploadProgress(null);
      window.requestAnimationFrame(scrollToBottom);
    } catch {
      setUploadProgress(null);
      setFailedAttachment(file);
      setAttachmentError('Не удалось загрузить вложение.');
    }
  }

  async function loadOlder() {
    if (!chat || !nextBefore) return;
    setLoadingOlder(true);
    try {
      const page = await customerCabinetApi.messagesPage(chat.id, nextBefore);
      setMessages((current) => mergeMessages(page.results, current));
      setNextBefore(page.next_before);
    } finally {
      setLoadingOlder(false);
    }
  }

  return (
    <MessageLayout screen="chat">
      <div className="customer-chat" data-state={state}>
        <CustomerPageHeading
          figmaNode="2680:40215"
          title="Сообщения"
          subtitle="Приватные чаты с перевозчиками по вашим заявкам"
        />
        <ChatAppContainer>
          <UnifiedChatSidebar
            chats={chats}
            partnerNameKey="carrier_name"
            query={query}
            selectedId={chat?.id}
            setQuery={setQuery}
            onSelectChat={(item) => {
              setActiveChatId(item.id);
              navigateCustomer('chat', { chat: item.id });
            }}
          />

          {chat ? (
            <div className="customer-chat__conversation">
              <UnifiedChatHeader
                partnerRole="carrier"
                partnerProfileId={chat.carrier}
                actions={(
                  <>
                    {transport ? (
                      <button
                        className="customer-chat__action-btn--transport"
                        type="button"
                        onClick={() => navigateCustomer('active-transport', { id: transport.id })}
                      >
                        К перевозке
                      </button>
                    ) : null}
                    {transport ? (
                      <button
                        className="customer-chat__action-btn--dispute"
                        type="button"
                        onClick={() => navigateCustomer('dispute', { deal: transport.deal })}
                      >
                        Спор
                      </button>
                    ) : null}
                  </>
                )}
                fromCity={chat.from_city}
                partnerName={chat.carrier_name}
                rating={offer ? offer.carrier_rating : null}
                requestCode={chat.request_code}
                socketState={socketState}
                toCity={chat.to_city}
              />

              <UnifiedMessageList
                currentUserId={auth.user?.id ?? 0}
                loadingOlder={loadingOlder}
                messages={messages}
                newMessagesCount={newMessages}
                nextBefore={nextBefore}
                viewportRef={messageViewport}
                onLoadOlder={() => void loadOlder()}
                onScrollToBottom={scrollToBottom}
              />

              <div className="customer-chat__payment-notice">
                {chat.phase === 'negotiation'
                  ? 'Приватные переговоры до выбора перевозчика. Стоимость и детали видны только участникам этого чата.'
                  : 'Оплата напрямую перевозчику — реквизиты согласуйте в чате. LOVO в платежах не участвует.'}
              </div>

              {chat.can_message ? (
                <UnifiedChatComposer
                  attachmentError={attachmentError}
                  disabled={state === 'sending'}
                  draft={draft}
                  sending={state === 'sending'}
                  uploadProgress={uploadProgress}
                  onSend={submit}
                  onSetDraft={setDraft}
                  onUploadFile={(file) => void attach(file)}
                />
              ) : (
                <div className="customer-chat__closed">
                  Переговоры завершены. История чата сохранена только для чтения.
                </div>
              )}
            </div>
          ) : null}
        </ChatAppContainer>
      </div>
    </MessageLayout>
  );
}

export function CustomerMessagesScreen({ screen }: { screen: CustomerMessageScreenId }) {
  return (
    <AuthGuard allow={['customer']}>
      {screen === 'chat-list' ? <ChatListView /> : <ConversationView />}
    </AuthGuard>
  );
}
